/**
 * A stored (not deflated) ZIP, built in memory. Media and APKs do not deflate.
 * No ZIP64 and no streaming, so callers check `canArchive` first.
 */

const LOCAL_HEADER = 0x04034b50;
const CENTRAL_HEADER = 0x02014b50;
const END_OF_DIRECTORY = 0x06054b50;

/** Stored, not deflated. */
const STORED = 0;

/** Say the bytes are UTF-8, so a name outside ASCII survives. */
const UTF8_NAMES = 0x800;

export interface ZipEntry {
  name: string;
  modified: Date;
  bytes: Uint8Array;
}

/** Built once, because every byte goes through it. */
const CRC_TABLE = (() => {
  const table = new Uint32Array(256);
  for (let i = 0; i < 256; i += 1) {
    let c = i;
    for (let k = 0; k < 8; k += 1) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
    table[i] = c >>> 0;
  }
  return table;
})();

export function crc32(bytes: Uint8Array): number {
  let crc = 0xffffffff;
  for (let i = 0; i < bytes.length; i += 1) {
    crc = CRC_TABLE[(crc ^ bytes[i]) & 0xff] ^ (crc >>> 8);
  }
  return (crc ^ 0xffffffff) >>> 0;
}

/** MS-DOS date and time. Two-second steps, nothing before 1980. */
function dosStamp(date: Date): { time: number; date: number } {
  // An invalid date writes zeros, which some readers refuse.
  const when = Number.isNaN(date.getTime()) ? new Date(0) : date;
  const year = Math.max(when.getFullYear(), 1980);
  return {
    time:
      (when.getHours() << 11) | (when.getMinutes() << 5) | (Math.floor(when.getSeconds() / 2) & 0x1f),
    date: ((year - 1980) << 9) | ((when.getMonth() + 1) << 5) | when.getDate(),
  };
}

function bytesOf(name: string): Uint8Array {
  return new TextEncoder().encode(name);
}

export function zipParts(entries: ZipEntry[]): Uint8Array[] {
  const parts: Uint8Array[] = [];
  const directory: Uint8Array[] = [];
  let offset = 0;

  for (const entry of entries) {
    const name = bytesOf(entry.name);
    const size = entry.bytes.length;

    // Offsets are 32-bit. Past 4 GB they wrap and the archive reads back wrong bytes.
    if (offset + 30 + name.length + size > 0xffffffff) {
      throw new Error('That selection is too large for one archive.');
    }

    const sum = crc32(entry.bytes);
    const stamp = dosStamp(entry.modified);

    const local = new DataView(new ArrayBuffer(30));
    local.setUint32(0, LOCAL_HEADER, true);
    local.setUint16(4, 20, true); // the version that understands stored entries
    local.setUint16(6, UTF8_NAMES, true);
    local.setUint16(8, STORED, true);
    local.setUint16(10, stamp.time, true);
    local.setUint16(12, stamp.date, true);
    local.setUint32(14, sum, true);
    local.setUint32(18, size, true);
    local.setUint32(22, size, true);
    local.setUint16(26, name.length, true);
    local.setUint16(28, 0, true);

    const header = new Uint8Array(local.buffer);
    parts.push(header, name, entry.bytes);

    const central = new DataView(new ArrayBuffer(46));
    central.setUint32(0, CENTRAL_HEADER, true);
    central.setUint16(4, 20, true);
    central.setUint16(6, 20, true);
    central.setUint16(8, UTF8_NAMES, true);
    central.setUint16(10, STORED, true);
    central.setUint16(12, stamp.time, true);
    central.setUint16(14, stamp.date, true);
    central.setUint32(16, sum, true);
    central.setUint32(20, size, true);
    central.setUint32(24, size, true);
    central.setUint16(28, name.length, true);
    central.setUint32(42, offset, true);
    directory.push(new Uint8Array(central.buffer), name);

    offset += header.length + name.length + size;
  }

  const directoryBytes = directory.reduce((sum, part) => sum + part.length, 0);

  const end = new DataView(new ArrayBuffer(22));
  end.setUint32(0, END_OF_DIRECTORY, true);
  end.setUint16(8, entries.length, true);
  end.setUint16(10, entries.length, true);
  end.setUint32(12, directoryBytes, true);
  end.setUint32(16, offset, true);

  return [...parts, ...directory, new Uint8Array(end.buffer)];
}

/** The archive as one run of bytes. */
export function buildZipBytes(entries: ZipEntry[]): Uint8Array {
  const all = zipParts(entries);
  const total = all.reduce((sum, part) => sum + part.length, 0);
  const out = new Uint8Array(total);
  let at = 0;
  for (const part of all) {
    out.set(part, at);
    at += part.length;
  }
  return out;
}

/** From the parts, so the archive is not copied once more before the Blob. */
export function buildZip(entries: ZipEntry[]): Blob {
  return new Blob(zipParts(entries) as BlobPart[], { type: 'application/zip' });
}

/** The entries and the archive are both in memory, so a tab fails well before 4 GB. */
export const ARCHIVE_LIMIT_BYTES = 512 * 1024 * 1024;

export function canArchive(sizes: (number | undefined)[]): boolean {
  // An unknown size counts as 64 MB, so it cannot slip past the limit.
  const total = sizes.reduce<number>((sum, size) => sum + (size ?? 64 * 1024 * 1024), 0);
  return total <= ARCHIVE_LIMIT_BYTES;
}

/** Two entries with one name would replace each other, so later ones get a number. */
export function uniqueNames(names: string[]): string[] {
  // Most computers hold Notes.txt and notes.txt as one file, so case does not count.
  const key = (name: string) => name.normalize('NFC').toLowerCase();
  // A number is never given to a name that another entry already has.
  const asked = new Set(names.map(key));
  const used = new Set<string>();

  return names.map((name) => {
    if (!used.has(key(name))) {
      used.add(key(name));
      return name;
    }

    const dot = name.lastIndexOf('.');
    const stem = dot > 0 ? name.slice(0, dot) : name;
    const extension = dot > 0 ? name.slice(dot) : '';
    for (let n = 2; ; n += 1) {
      const candidate = `${stem}-${n}${extension}`;
      if (!used.has(key(candidate)) && !asked.has(key(candidate))) {
        used.add(key(candidate));
        return candidate;
      }
    }
  });
}
